import dayjs from 'dayjs';
import * as z from 'zod/v4';

const dateFieldSchema = z.preprocess((val) => (dayjs.isDayjs(val) ? val.toISOString() : val), z.string().nullable());

const stringNumberFieldSchema = z.preprocess((val) => {
  if (val === '' || val === null || val === undefined) return null;
  if (typeof val === 'string') return Number(val);
  return val;
}, z.number().multipleOf(0.01, { message: 'Max 2 decimal places' }).nullable());

export const ProjectModalSchema = z.object({
  name: z.string().min(3, { message: 'Project name is required' }),
  project_id: z.string().optional(),
  manager: z.array(z.string()).min(1, { message: 'Please select a manager' }),
  status: z.string().min(1, { message: 'Please select a status' }),
  client_contact: z.string().min(1, { message: 'Please select a client' }),
  start_date: dateFieldSchema,
  finish_date: dateFieldSchema,
  estimated_cost: stringNumberFieldSchema,
  description: z.string().optional(),
  percent_complete: z.number(),
  current_scope: z.string().nullable(),
  estimated_hours: stringNumberFieldSchema,
  current_estimated_cost: stringNumberFieldSchema,
  current_estimated_hours: stringNumberFieldSchema,
});

export const AddProjectModalSchema = z.object({
  name: z.string().min(3, { message: 'Name is required' }),
  client_contact: z.string().min(1, { message: 'Client is required' }),
});

export const ProjectScopeSchema = z.object({
  id: z.string().optional(),
  project: z.string().optional(),
  parent_scope: z.string().nullable().optional(),
  version: z.string().min(1, { message: 'Version is required' }),
  status: z.string().min(1, { message: 'Please select a status' }),
  change_summary: z.string().min(1, { message: 'Change summary is required' }),
  cost_delta: stringNumberFieldSchema,
  hours_delta: stringNumberFieldSchema,
  effective_from: dateFieldSchema,
  effective_till: dateFieldSchema,
  is_client_approved: z.boolean(),
});

export type ProjectScopeFormValues = z.infer<typeof ProjectScopeSchema>;

export const AddProjectClientSchema = z.object({
  id: z.string().optional(),
  client: z.string().min(1, { message: 'Client is required' }),
  full_name: z.string().min(3, { message: 'Full name is required' }),
  position: z.string().optional(),
  email: z.email(),
  phone: z.string().optional(),
});

// Job Schema

export const AddEditJobSchema = z.object({
  job_id: z.string().optional(),
  job_associated_quote: z.string().optional(),
  client_id: z.string().min(1, { message: 'Client is required' }),
  job_manager: z.string().min(1, { message: 'Please select a job manager' }),
  teams: z.string().nullable(),
  activities: z.array(
    z.object({
      activityId: z.string(),
      activityName: z.string(),
    }),
  ),
  invoice_header: z.string().min(1, { message: 'Invoice header is required' }),
  job_content_type: z.string().optional(),
  job_description: z.string().optional(),
  job_status: z.string().min(1, { message: 'Please select a status' }),
  job_name: z.string().min(3, { message: 'Job name is required' }),
  job_type: z.string().min(1, { message: 'Please select a job type' }),
  job_modified: z.boolean().optional(),
  is_active: z.boolean(),
  start_date: dateFieldSchema,
  end_date: dateFieldSchema,
  introduced_in_scope_id: z.string().min(1, { message: 'Please select a Project Scope' }),
  removed_in_scope: z.string(),
  percent_complete: z.number(),
  job_billing_cycle: z.string().min(1, { message: 'Billing cycle is required' }),
  priority: z.string().min(1, { message: 'Please select a priority' }),
});

export type JobFormValues = z.infer<typeof AddEditJobSchema>;

export const AddEditTaskSchema = z.object({
  name: z.string({ error: 'Task name is required' }).min(1, { message: 'Task name is required' }),
  job_id: z.string().optional(),
  status: z.string({ error: 'Status is required' }).min(1, { message: 'Status is required' }),
  priority: z.string().min(1, { message: 'Please select a priority' }),
  description: z.string().optional(),
  assigned_to_id: z.string().min(1, { message: 'Must assign task to a slave' }),
  estimated_hours: stringNumberFieldSchema,
  actual_hours: stringNumberFieldSchema,
  due_date: dateFieldSchema,
  introduced_in_scope_id: z.string().min(1, { message: 'Project Scope is required' }),
  removed_in_scope: z.string().nullable(),
  percent_complete: z.string().optional(),
  is_active: z.boolean(),
  id: z.string().optional(),
});

export type TaskFromValues = z.infer<typeof AddEditTaskSchema>;

export const AddressSchema = z.object({
  street: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  postal_code: z.string().nullable(),
  phone_number: z.string().nullable(),
  fax: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  contact_name: z.string().nullable(),
});

export const AddEditClientSchema = z.object({
  client_id: z.string().optional(),
  client_name: z.string().min(1, { message: 'Client name is required' }),
  is_individual: z.boolean().optional(),
  is_active: z.boolean().optional(),
  display_id: z.string().optional(),
  hourly_billing_rate: z.number().optional(),
  tax_code: z.string().min(1, { message: 'Please select a tax code' }),
  client_contact_firstname: z.string().nullable(),
  client_contact_lastname: z.string().nullable(),
  address: AddressSchema.optional(),
});

export type ClientFormValues = z.infer<typeof AddEditClientSchema>;

export const AddTaxCodeSchema = z.object({
  code: z.string().min(1, { message: 'Code is required' }),
  description: z.string().min(1, { message: 'Description is required' }),
  type: z.string().min(1, { message: 'Type is required' }),
  rate: z.string().min(1, { message: 'Rate is required' }),
});

export type TaxCodeFormValues = z.infer<typeof AddTaxCodeSchema>;

// Employee Schema

export const AddEditEmployeeSchema = z.object({
  employee_id: z.string().optional(),
  firstname: z.string().min(1, { message: 'First name is required' }),
  lastname: z.string().min(1, { message: 'Last name is required' }),
  username: z.string().min(1, { message: 'Username is required' }),
  email: z.email({ message: 'A valid email is required' }),
  user_role: z.string().min(1, { message: 'Please select a role' }),
  is_active: z.boolean(),
  team_id: z.string().nullable(),
  activity_ids: z.array(z.string()),
  employment_status: z.string().min(1, { message: 'Please select an employment status' }),
  employment_category: z.string().min(1, { message: 'Please select an employment category' }),
  address: AddressSchema.optional(),
});

export type EmployeeFormValues = z.infer<typeof AddEditEmployeeSchema>;
